// Libs
import {
    keepPreviousData,
    useMutation,
    type UseMutationResult,
    useQuery,
    type UseQueryResult,
} from "@tanstack/react-query";
import type { OrganizationSideMenu } from "src/layouts/AdminMonitoringLayout";

// Types
import type { EnhancedOrganization } from "src/types/organization";
import type { ThirdServiceCategory } from "src/types/posCredential";
import { Query } from "src/types/query";

// Utils
import axios from "src/utils/axios";

export const useQueryAdminMonitoringGetOrganizationList = (): UseQueryResult<
    {
        organizationsWithAlert: OrganizationSideMenu[];
        organizationsWithError: OrganizationSideMenu[];
    },
    unknown
> => {
    return useQuery({
        queryKey: [Query.AdminMonitoring],

        queryFn: async () => {
            const { data } = await axios.get<{
                organizationsWithAlert: EnhancedOrganization[];
                organizationsWithError: EnhancedOrganization[];
            }>(`/admin-monitoring/organization-with-alerts`);
            return {
                organizationsWithError: (data?.organizationsWithError ?? []).map(
                    (organization) => ({
                        title: organization.name,
                        icon: null,
                        href: `/app/superadmin-monitoring/dashboard/${organization.id}`,
                        ownerId: organization?.owner?.id,
                        id: organization.id,
                    })
                ),
                organizationsWithAlert: (data?.organizationsWithAlert ?? []).map(
                    (organization) => ({
                        title: organization.name,
                        icon: null,
                        href: `/app/superadmin-monitoring/dashboard/${organization.id}`,
                        ownerId: organization?.owner?.id,
                        id: organization.id,
                    })
                ),
            };
        },
    });
};

export const useQueryAdminMonitoringGetAlertsByOrganizationAndServices = (
    organizationId: number,
    services: ThirdServiceCategory[] = []
): UseQueryResult<any[], unknown> => {
    return useQuery({
        queryKey: [Query.AdminMonitoring, organizationId, JSON.stringify(services)],
        queryFn: async () =>
            (
                await axios.get(`/admin-monitoring/monitoring-data/${organizationId}`, {
                    params: { services },
                })
            ).data,
        placeholderData: keepPreviousData,
    });
};

export const useRefreshOrganizationAlertById = (
    organizationId: number
): UseMutationResult<unknown, Error, number, unknown> => {
    return useMutation({
        mutationKey: [Query.AdminMonitoring, organizationId],
        mutationFn: async (organizationId: number) =>
            await axios.get(`/admin-monitoring/monitoring-data-fetch-by-id/${organizationId}`),
    });
};
